import type { PmState } from '../chat/usePrivateMessages.js';
import { useState } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { GameIcon } from '../components/GameIcon.js';
import { ArenaHero } from '../components/Logo.js';
import { LobbyChat } from '../components/LobbyChat.js';
import { RadioPlayer } from '../components/RadioPlayer.js';
import { BingoLobbyCard } from '../components/BingoLobbyCard.js';
import { TablesStrip } from '../components/TablesStrip.js';
import { RulesDialog } from '../components/RulesDialog.js';
import { SnakeSolo } from '../games/snake/SnakeSolo.js';
import { TetraxSolo } from '../games/tetrax/TetraxSolo.js';
import { ThemeSwitch, useSnakeTheme } from '../games/snake/SnakeBoard.js';
import { formatDuration, useServerNow, type ArenaState } from '../arena/useArena.js';
import type { ArenaSocket, Realtime } from '../realtime.js';
import {
  AI_LEVELS,
  CHESS_CLOCKS,
  DEFAULT_AI_LEVEL,
  DEFAULT_CHESS_CLOCK,
  GAME_INFO,
  GAME_TYPES,
  type ArenaResult,
  type GameOptions,
  type GameType,
  type LobbyPlayer,
  type PublicUser,
} from '../../shared/protocol.js';
import { useChessTheme } from '../games/chess/ChessGame.js';

type Flash = { ok: boolean; text: string } | null;

function arenaError(result: ArenaResult): string {
  return result.ok ? '' : t.arena.errors[result.error];
}

/** Lobby: game selection, players, challenges (suunnitelma.md sections 5–8). */
export function ArenaView(props: { user: PublicUser; realtime: Realtime; arena: ArenaState; pm: PmState }) {
  const { arena, realtime } = props;
  const socket = realtime.socket;
  const [selectedGame, setSelectedGame] = useState<GameType>('battleship');
  const [flash, setFlash] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);
  const [showRules, setShowRules] = useState(false);
  // Snake and Tetrax alone (2026-09-30, 2026-10-03): played in the browser, the lobby stays behind it.
  const [solo, setSolo] = useState<GameType | null>(null);
  const [snakeTheme, setSnakeTheme] = useSnakeTheme();
  // Chess (decision 2026-09-30): clock and computer level are chosen before the game.
  const [chessClock, setChessClock] = useState(CHESS_CLOCKS.findIndex((c) => c.baseMin === DEFAULT_CHESS_CLOCK.baseMin && c.incSec === DEFAULT_CHESS_CLOCK.incSec));
  const [aiLevel, setAiLevel] = useState<number>(DEFAULT_AI_LEVEL);
  const [chessTheme, setChessTheme] = useChessTheme();
  const serverNow = useServerNow(realtime.clockOffset, 500);

  const me = arena.me;
  if (!socket || !me) return <section class="card muted">{t.status.connecting}</section>;
  if (solo === 'tetrax') return <TetraxSolo onExit={() => setSolo(null)} />;
  if (solo) return <SnakeSolo onExit={() => setSolo(null)} />;

  const emit = async (fn: (s: ArenaSocket, done: (r: ArenaResult) => void) => void) => {
    setBusy(true);
    setFlash(null);
    const result = await new Promise<ArenaResult>((resolve) => fn(socket, resolve));
    setBusy(false);
    if (!result.ok) setFlash({ ok: false, text: arenaError(result) });
    return result;
  };

  const challenge = (p: LobbyPlayer) =>
    emit((s, done) => s.emit('challenge:send', { toUserId: p.id, gameType: selectedGame, options: options() }, done));
  const options = (): GameOptions | undefined => (selectedGame === 'chess' ? { clock: CHESS_CLOCKS[chessClock], aiLevel } : undefined);
  const random = () => emit((s, done) => s.emit('challenge:random', { gameType: selectedGame, options: options() }, done));
  const setAccepts = (accepts: boolean) => emit((s, done) => s.emit('presence:setAccepts', accepts, done));

  const others = arena.players.filter((p) => p.id !== props.user.id);
  const outgoing = me.outgoing;
  // Maintenance break coming or on (decision 2026-09-27): no new games for players.
  const phase = arena.closure?.phase;
  const gamesBlocked = props.user.role !== 'admin' && (phase === 'draining' || phase === 'closed');

  return (
    <>
    <ArenaHero subtitle={t.arena.heroSubtitle} />
    <TablesStrip socket={socket} arena={arena} serverNow={serverNow} canJoin={!gamesBlocked && !me.restrictions.gameBan} />
    <BingoLobbyCard />
    <div class="arena">
      <section class="card arena-games">
        <h2>{t.arena.chooseGame}</h2>
        <div class="game-grid">
          {GAME_TYPES.map((g) => (
            <button
              class={`game-card ${selectedGame === g ? 'selected' : ''}`}
              onClick={() => setSelectedGame(g)}
              aria-pressed={selectedGame === g}
            >
              <GameIcon game={g} />
              <span class="game-name">{t.games[g].name}</span>
              <span class="game-desc">{t.games[g].description}</span>
            </button>
          ))}
        </div>
        <button class="btn btn-small rules-btn" onClick={() => setShowRules(true)}>
          📖 {t.rules.buttonFor(t.games[selectedGame].name)}
        </button>
        {/* Snake: the look is chosen before the game (user request 2026-09-30). */}
        {selectedGame === 'snake' && <ThemeSwitch theme={snakeTheme} onChange={setSnakeTheme} />}
        {selectedGame === 'chess' && (
          <div class="chess-options">
            <label>
              {t.chess.clock}
              <select value={chessClock} onChange={(e) => setChessClock(Number(e.currentTarget.value))}>
                {CHESS_CLOCKS.map((c, i) => (
                  <option value={i}>{t.chess.clockLabel(c.baseMin, c.incSec)}</option>
                ))}
              </select>
            </label>
            <label>
              {t.chess.level}
              <select value={aiLevel} onChange={(e) => setAiLevel(Number(e.currentTarget.value))}>
                {AI_LEVELS.map((l) => (
                  <option value={l}>
                    {l} – {t.chess.levels[l]}
                  </option>
                ))}
              </select>
            </label>
            <div class="snake-theme" role="group" aria-label={t.chess.theme}>
              <span class="muted">{t.chess.theme}:</span>
              {(['wood', 'dark'] as const).map((th) => (
                <button type="button" class={`btn btn-small ${chessTheme === th ? 'btn-primary' : ''}`} aria-pressed={chessTheme === th} onClick={() => setChessTheme(th)}>
                  {t.chess.themes[th]}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section class="card arena-players">
        <div class="section-head">
          <h2>{t.arena.players(t.games[selectedGame].name)}</h2>
          <label class="switch">
            <input type="checkbox" checked={me.acceptsChallenges} onChange={(e) => setAccepts(e.currentTarget.checked)} />
            <span class="switch-track" aria-hidden="true" />
            {t.arena.acceptChallenges}
          </label>
        </div>

        {me.restrictions.gameBan && (
          <p class="error">
            {t.restrictions.gameBanned(new Date(me.restrictions.gameBan.until).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' }))}
          </p>
        )}
        <div class="me-status">
          <span class={`status-dot status-${me.status}`} />
          {t.arena.yourStatus}: <strong>{t.arena.status[me.status]}</strong>
        </div>

        {outgoing ? (
          <div class="outgoing">
            <div>
              {t.arena.waitingFor(outgoing.to.displayName)}
              <div class="bar">
                <div class="bar-fill" style={{ width: `${Math.max(0, Math.min(100, ((outgoing.expiresAt - serverNow) / 60000) * 100))}%` }} />
              </div>
            </div>
            <span class="countdown">{formatDuration(outgoing.expiresAt - serverNow)}</span>
            <button
              class="btn btn-small"
              disabled={busy}
              onClick={() => emit((s, done) => s.emit('challenge:cancel', { id: outgoing.id }, done))}
            >
              {t.arena.cancel}
            </button>
          </div>
        ) : (
          <div class="start-buttons">
            <button class="btn btn-primary" onClick={random} disabled={busy || gamesBlocked}>
              {t.arena.randomOpponent}
            </button>
            {GAME_INFO[selectedGame].computerOpponent && (
              <button
                class="btn"
                onClick={() => emit((s, done) => s.emit('game:startComputer', { gameType: selectedGame, options: options() }, done))}
                disabled={busy || gamesBlocked}
              >
                {t.arena.playComputer}
              </button>
            )}
            {GAME_INFO[selectedGame].solo && (
              <button class="btn" onClick={() => setSolo(selectedGame)}>
                {t.snake.playSolo}
              </button>
            )}
          </div>
        )}

        {flash && (
          <p class={flash.ok ? 'success' : 'error'} role="status">
            {flash.text}
          </p>
        )}

        {others.length === 0 ? (
          <p class="muted empty">{t.arena.noPlayers}</p>
        ) : (
          <ul class="player-list">
            {others.map((p) => {
              const canChallenge = p.status === 'available' && !outgoing && !gamesBlocked;
              return (
                <li class="player-row">
                  <span class={`status-dot status-${p.status}`} title={t.arena.status[p.status]} />
                  <span class="player-name">
                    {p.displayName}
                    {p.guest ? (
                      <span class="player-level guest-tag" title={t.guest.badgeTitle}>
                        {t.guest.badge}
                      </span>
                    ) : (
                      <span class="player-level">{t.levels[p.level]}</span>
                    )}
                  </span>
                  <span class="player-status">{t.arena.status[p.status]}</span>
                  <button class="btn btn-small" disabled={!canChallenge || busy} onClick={() => challenge(p)}>
                    {t.arena.challenge}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <div class="arena-chat">
        <RadioPlayer />
        <LobbyChat socket={socket} me={props.user} players={arena.players} restrictions={me.restrictions} pm={props.pm} />
      </div>
    </div>
    {showRules && <RulesDialog game={selectedGame} onClose={() => setShowRules(false)} />}
    </>
  );
}
